import uploadFileIcon from '@assets/misc-icons/file.svg';
import { AllowedFileType } from '@global/constants/general';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { ReactNode, useEffect, useRef, useState } from 'react';
import styles from './styles';

interface UploadFileProps {
  label: string;
  disabled?: boolean;
  handleFiles?: (e: any) => void;
  onClick?: () => void;
  error: boolean;
  fileTypes?: AllowedFileType[] | undefined;
  sx?: any;
  acceptMultipleFiles?: boolean;
  loading?: boolean;
  allowDragAndDrop?: boolean;
  icon?: ReactNode;
  openOnMount?: boolean;
}

function UploadFile(props: UploadFileProps) {
  const {
    label,
    disabled,
    handleFiles,
    onClick,
    error,
    fileTypes,
    sx,
    acceptMultipleFiles = true,
    loading,
    allowDragAndDrop = true,
    icon,
    openOnMount = false
  } = props;
  const uploadInputRef = useRef<any>(null);
  const [highlightDragBox, setHighlightDragBox] = useState(false);
  const allowedTypes: string[] = fileTypes ?? Object.values(AllowedFileType);
  const addnStyles = {
    borderColor: error ? 'error.main' : '#E7EDF6',
    ...sx
  };
  useEffect(() => {
    if (openOnMount) {
      if (uploadInputRef.current) {
        uploadInputRef.current.click();
      }
    }
  }, []);
  return (
    <>
      <Button
        sx={
          highlightDragBox
            ? {
                ...styles.addFileBtn,
                backgroundColor: 'background.third',
                transition: '.22s background ease-out',
                ...addnStyles
              }
            : { ...styles.addFileBtn, ...addnStyles }
        }
        onClick={() => {
          if (onClick) {
            onClick();
          }
          if (uploadInputRef.current && handleFiles) {
            uploadInputRef.current.click();
          }
        }}
        disabled={disabled || loading}
        onDragEnter={() => {
          // setHighlightDragBox(true);
        }}
        onDragLeave={() => {
          if (allowDragAndDrop) {
            setHighlightDragBox(false);
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (allowDragAndDrop) {
            setHighlightDragBox(true);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (allowDragAndDrop) {
            setHighlightDragBox(false);
            if (onClick) {
              onClick();
            }
            const filteredFiles = Array.from(e.dataTransfer.files).filter((file) =>
              allowedTypes.includes(file.type)
            );
            if (filteredFiles?.length === 0) {
              enqueueSnackbar(`Allowed file types: ${allowedTypes.join(', ')}`, {
                variant: 'error'
              });
              return;
            }
            if (handleFiles) {
              handleFiles(filteredFiles);
            }
          }
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: '1.25rem',
            width: '85%'
          }}>
          <div
            style={{
              height: '2.8rem',
              width: '2.8rem',
              backgroundColor: '#FFEFDC',
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              placeItems: 'center',
              padding: '.75rem'
            }}>
            {icon ?? (
              <img
                src={uploadFileIcon}
                style={{ height: '1.9rem', width: 'auto' }}
                alt="Upload File Icon"
              />
            )}
          </div>
          <Typography
            variant="body2"
            sx={{
              color: error ? 'error.main' : '#8F8F92',
              fontWeight: '400',
              overflow: 'ellipsis',
              width: '100%',
              textAlign: 'left',
              opacity: disabled ? 0.5 : 1
            }}
            noWrap>
            {label}
          </Typography>
        </Box>
        {loading ? (
          <Box>
            <CircularProgress style={{ height: '1.7rem', width: '1.7rem', marginLeft: '.5rem' }} />
          </Box>
        ) : (
          <ArrowUpwardIcon sx={{ height: '2rem', width: 'auto', ml: '.5rem' }} />
        )}
      </Button>
      <input
        hidden
        accept={allowedTypes.join(',')}
        type="file"
        onChange={(e) => {
          const files = e.target.files;
          if (!files) return;
          const filteredFiles = Array.from(files).filter((file) =>
            allowedTypes.includes(file.type)
          );
          if (filteredFiles.length !== files.length) {
            const allowedTypesMessage = allowedTypes
              .map((type: string) => {
                const parts = type.split('/');
                return parts[1] ? parts[1] : type;
              })
              .join(', ');
            enqueueSnackbar(`Only ${allowedTypesMessage} files are allowed`, {
              variant: 'error'
            });
          }
          if (handleFiles) {
            handleFiles(filteredFiles);
          }
        }}
        multiple={acceptMultipleFiles}
        onClick={(event) => {
          event.currentTarget.value = '';
        }}
        ref={uploadInputRef}
        disabled={disabled || loading}
      />
    </>
  );
}

export default UploadFile;
